import React, { useState } from 'react';
import { X, Plus, Save } from 'lucide-react';
import { useAppContext } from '../context/AppContext';

/**
 * Editable list of restaurant packages (VIP, Brunch, Afternoon Tea...).
 * Packages change often, so they live in the restaurant_packages table
 * rather than in code. Retire a package by unticking Active (keeps history).
 */
export default function PackageManager({ onClose }) {
  const { restaurantPackages, addRestaurantPackage, updateRestaurantPackage } = useAppContext();
  const [drafts, setDrafts] = useState({});
  const [newPkg, setNewPkg] = useState({ name: '', inclusions: '', pricePp: '', kitchenOrderDayBefore: false });
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState('');

  const val = (p, key) => (drafts[p.id] && drafts[p.id][key] !== undefined ? drafts[p.id][key] : p[key]);
  const setVal = (id, key, v) => setDrafts((d) => ({ ...d, [id]: { ...(d[id] || {}), [key]: v } }));

  const save = async (p) => {
    if (!drafts[p.id]) return;
    setSaving(p.id); setError('');
    try {
      await updateRestaurantPackage(p.id, drafts[p.id]);
      setDrafts((d) => { const n = { ...d }; delete n[p.id]; return n; });
    } catch (e) { setError(e.message); }
    setSaving(null);
  };

  const add = async () => {
    if (!newPkg.name.trim()) return;
    setSaving('new'); setError('');
    try {
      const maxSort = Math.max(0, ...restaurantPackages.map((p) => p.sortOrder || 0));
      await addRestaurantPackage({ ...newPkg, name: newPkg.name.trim(), sortOrder: maxSort + 10, active: true });
      setNewPkg({ name: '', inclusions: '', pricePp: '', kitchenOrderDayBefore: false });
    } catch (e) { setError(e.message); }
    setSaving(null);
  };

  const input = 'border border-gray-300 rounded px-2 py-1 text-sm w-full';

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Restaurant Packages</h2>
            <p className="text-xs text-gray-500">Shown on table bookings and printed with what's included on the daily function sheet.</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1"><X size={20} /></button>
        </div>
        <div className="p-5">
          {error && <div className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>}
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-gray-500">
                <th className="py-1 pr-2">Package</th>
                <th className="py-1 pr-2">What's included</th>
                <th className="py-1 pr-2 w-24">€ pp</th>
                <th className="py-1 pr-2 w-28" title="Kitchen must order the day before (e.g. afternoon tea)">Order day before</th>
                <th className="py-1 pr-2 w-16">Active</th>
                <th className="py-1 w-16"></th>
              </tr>
            </thead>
            <tbody>
              {restaurantPackages.map((p) => (
                <tr key={p.id} className={`border-t ${val(p, 'active') ? '' : 'opacity-50'}`}>
                  <td className="py-1.5 pr-2"><input className={input} value={val(p, 'name')} onChange={(e) => setVal(p.id, 'name', e.target.value)} /></td>
                  <td className="py-1.5 pr-2"><input className={input} value={val(p, 'inclusions')} onChange={(e) => setVal(p.id, 'inclusions', e.target.value)} /></td>
                  <td className="py-1.5 pr-2"><input className={input} type="number" step="0.5" value={val(p, 'pricePp') ?? ''} onChange={(e) => setVal(p.id, 'pricePp', e.target.value)} /></td>
                  <td className="py-1.5 pr-2 text-center"><input type="checkbox" checked={!!val(p, 'kitchenOrderDayBefore')} onChange={(e) => setVal(p.id, 'kitchenOrderDayBefore', e.target.checked)} /></td>
                  <td className="py-1.5 pr-2 text-center"><input type="checkbox" checked={!!val(p, 'active')} onChange={(e) => setVal(p.id, 'active', e.target.checked)} /></td>
                  <td className="py-1.5">
                    {drafts[p.id] && (
                      <button onClick={() => save(p)} disabled={saving === p.id} className="inline-flex items-center gap-1 bg-emerald-700 text-white rounded px-2 py-1 text-xs font-semibold">
                        <Save size={12} /> {saving === p.id ? '…' : 'Save'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              <tr className="border-t bg-gray-50">
                <td className="py-1.5 pr-2"><input className={input} placeholder="New package name" value={newPkg.name} onChange={(e) => setNewPkg({ ...newPkg, name: e.target.value })} /></td>
                <td className="py-1.5 pr-2"><input className={input} placeholder="e.g. 2 courses + cocktail" value={newPkg.inclusions} onChange={(e) => setNewPkg({ ...newPkg, inclusions: e.target.value })} /></td>
                <td className="py-1.5 pr-2"><input className={input} type="number" step="0.5" value={newPkg.pricePp} onChange={(e) => setNewPkg({ ...newPkg, pricePp: e.target.value })} /></td>
                <td className="py-1.5 pr-2 text-center"><input type="checkbox" checked={newPkg.kitchenOrderDayBefore} onChange={(e) => setNewPkg({ ...newPkg, kitchenOrderDayBefore: e.target.checked })} /></td>
                <td></td>
                <td className="py-1.5">
                  <button onClick={add} disabled={!newPkg.name.trim() || saving === 'new'} className="inline-flex items-center gap-1 bg-blue-600 disabled:bg-gray-300 text-white rounded px-2 py-1 text-xs font-semibold">
                    <Plus size={12} /> Add
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
          <p className="mt-3 text-xs text-gray-500">To retire a package, untick Active. Old bookings keep the name they were booked with.</p>
        </div>
      </div>
    </div>
  );
}
